import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTranslationApp } from "../../lib/i18next";
import {
  EVENT_DETAILS_NETWORK_FACEBOOK,
  EVENT_DETAILS_NETWORK_INSTAGRAM,
  EVENT_DETAILS_NETWORK_LINKEDIN,
  EVENT_DETAILS_NETWORK_TIKTOK,
  EVENT_DETAILS_NETWORK_YOUTUBE,
  EVENT_DETAILS_STATUS_PUBLISHED,
  GALLERY_PLATFORM_STATUSES_LABEL,
  GALLERY_STATUS_FAILED,
  GALLERY_STATUS_FAILED_UNKNOWN,
  GALLERY_STATUS_PUBLISHED_DIRECT,
  GALLERY_STATUS_PUBLISHED_DIRECT_UNKNOWN,
  GALLERY_STATUS_PUBLISHED_LEGACY,
  GALLERY_STATUS_PUBLISHED_SCHEDULE,
  GALLERY_STATUS_PUBLISHED_SCHEDULE_UNKNOWN,
  GALLERY_STATUS_SCHEDULED,
  GALLERY_STATUS_SCHEDULED_UNKNOWN,
} from "../../i18n/keysTranslations";
import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  TIKTOK,
  YOUTUBE,
} from "../../utils/constants";
import {
  PUBLICATION_SOURCE,
  PUBLICATION_STATUS,
} from "../../utils/contentPlatformStatuses";
import { toZonedDisplayDate } from "../../utils/timezone";
import PlatformLogo from "../Common/PlatformLogo";

const PLATFORM_NAME_KEYS = {
  [INSTAGRAM]: EVENT_DETAILS_NETWORK_INSTAGRAM,
  [FACEBOOK]: EVENT_DETAILS_NETWORK_FACEBOOK,
  [TIKTOK]: EVENT_DETAILS_NETWORK_TIKTOK,
  [YOUTUBE]: EVENT_DETAILS_NETWORK_YOUTUBE,
  [LINKEDIN]: EVENT_DETAILS_NETWORK_LINKEDIN,
};

const STATUS_GROUP_ORDER = [
  PUBLICATION_STATUS.PUBLISHED,
  PUBLICATION_STATUS.SCHEDULED,
  PUBLICATION_STATUS.FAILED,
];

const STATUS_GROUP_STYLES = {
  [PUBLICATION_STATUS.PUBLISHED]: {
    pillBackground: "#e6f5ee",
    dot: "#29a66d",
    color: "#177a50",
    labelKey: EVENT_DETAILS_STATUS_PUBLISHED,
  },
  [PUBLICATION_STATUS.SCHEDULED]: {
    pillBackground: "#fff4e5",
    dot: "#d97706",
    color: "#9a5b0b",
    labelKey: GALLERY_STATUS_SCHEDULED_UNKNOWN,
  },
  [PUBLICATION_STATUS.FAILED]: {
    pillBackground: "#fdecec",
    dot: "#dc2626",
    color: "#b42318",
    labelKey: GALLERY_STATUS_FAILED_UNKNOWN,
  },
};

const formatStatusDate = ({ timestamp, timezone, locale }) => {
  const zoned = toZonedDisplayDate({ date: timestamp, timezone });
  if (!zoned) return null;
  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(zoned);
};

const buildStatusTooltip = ({ status, t, timezone, locale }) => {
  const platformName = t(
    PLATFORM_NAME_KEYS[status.platform] || status.platform
  );

  const withPlatform = (detail) => `${platformName}: ${detail}`;

  switch (status.status) {
    case PUBLICATION_STATUS.SCHEDULED: {
      const date = formatStatusDate({
        timestamp: status.scheduledAt,
        timezone,
        locale,
      });
      return withPlatform(
        date
          ? t(GALLERY_STATUS_SCHEDULED, { date })
          : t(GALLERY_STATUS_SCHEDULED_UNKNOWN)
      );
    }
    case PUBLICATION_STATUS.FAILED: {
      const date = formatStatusDate({
        timestamp: status.failedAt,
        timezone,
        locale,
      });
      return withPlatform(
        date
          ? t(GALLERY_STATUS_FAILED, { date })
          : t(GALLERY_STATUS_FAILED_UNKNOWN)
      );
    }
    case PUBLICATION_STATUS.PUBLISHED: {
      switch (status.source) {
        case PUBLICATION_SOURCE.SCHEDULE: {
          const date = formatStatusDate({
            timestamp: status.publishedAt,
            timezone,
            locale,
          });
          return withPlatform(
            date
              ? t(GALLERY_STATUS_PUBLISHED_SCHEDULE, { date })
              : t(GALLERY_STATUS_PUBLISHED_SCHEDULE_UNKNOWN)
          );
        }
        case PUBLICATION_SOURCE.DIRECT: {
          const date = formatStatusDate({
            timestamp: status.publishedAt,
            timezone,
            locale,
          });
          return withPlatform(
            date
              ? t(GALLERY_STATUS_PUBLISHED_DIRECT, { date })
              : t(GALLERY_STATUS_PUBLISHED_DIRECT_UNKNOWN)
          );
        }
        default:
          return withPlatform(t(GALLERY_STATUS_PUBLISHED_LEGACY));
      }
    }
    default:
      return withPlatform(t(GALLERY_STATUS_PUBLISHED_LEGACY));
  }
};

const groupStatusesByStatus = (statuses = []) => {
  const grouped = new Map();
  statuses.forEach((status) => {
    if (!STATUS_GROUP_STYLES[status.status]) return;
    const current = grouped.get(status.status) || [];
    current.push(status);
    grouped.set(status.status, current);
  });

  return STATUS_GROUP_ORDER.filter((status) => grouped.has(status)).map(
    (status) => ({
      status,
      items: grouped.get(status),
      styles: STATUS_GROUP_STYLES[status],
    })
  );
};

const StatusGroup = ({ group, t, timezone, locale }) => {
  const label = t(group.styles.labelKey);

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "10px",
        color: group.styles.color,
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          px: "13px",
          py: "8px",
          borderRadius: "999px",
          backgroundColor: group.styles.pillBackground,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="span"
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: group.styles.dot,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 10 }}>{label}</span>
      </Box>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: "1px",
          height: "27px",
          backgroundColor: "#d8dce0",
          flexShrink: 0,
        }}
      />
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {group.items.map((status) => {
          const tooltip = buildStatusTooltip({
            status,
            t,
            timezone,
            locale,
          });
          const key = `${status.platform}:${status.accountID}:${
            status.scheduleID || status.source
          }`;

          return (
            <Tooltip key={key} title={tooltip} arrow placement="top">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <PlatformLogo
                  platform={status.platform}
                  width={22}
                  height={22}
                  statusIndicator={status.status}
                  alt=""
                  aria-label={tooltip}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

const ContentPlatformStatuses = ({ statuses = [], timezone }) => {
  const { t, i18n } = useTranslationApp();
  const groups = groupStatusesByStatus(statuses);

  if (groups.length === 0) return null;

  const groupLabel = t(GALLERY_PLATFORM_STATUSES_LABEL);
  const locale = i18n?.language;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        width: "100%",
        minHeight: 34,
        px: "9px",
        py: "6px",
        overflow: "visible",
        borderTop: "1px solid #f0f1f3",
        backgroundColor: "#fbfcfd",
      }}
      aria-label={groupLabel}
    >
      {groups.map((group) => (
        <StatusGroup
          key={group.status}
          group={group}
          t={t}
          timezone={timezone}
          locale={locale}
        />
      ))}
    </Box>
  );
};

export default ContentPlatformStatuses;
